import {Injectable} from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject
} from "rxjs";
import {
  filter,
  map
} from "rxjs/operators";
import { OAuthService} from "angular-oauth2-oidc";
import {Router} from "@angular/router";
import {LocalStorageWrapper} from "../utils/local-storage-wrapper";
import {NgxIndexedDBService} from "ngx-indexed-db";
import * as Sentry from "@sentry/angular";

@Injectable({providedIn: 'root'})
export class AuthService {
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage() {
    // TODO: Remember current URL
    this.router.navigateByUrl('/should-login');
  }

  constructor(
      private oauthService: OAuthService, private router: Router, private indexDBService: NgxIndexedDBService) {
    // Useful for debugging:

    this.oauthService.setStorage(new LocalStorageWrapper());

    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup.
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this.oauthService.events
        .subscribe(_ => {
          this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
        });

    /*    this.oauthService.events
          .pipe(filter(e => ['token_received'].includes(e.type)))
          .subscribe(() => this.oauthService.loadUserProfile());*/

    this.oauthService.events
        .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
        .subscribe(() => this.navigateToLoginPage());

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public async runInitialLoginSequence(): Promise<void> {
    console.log("Running initial login sequence");
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(location.hash.substr(1).split('&').map(kvp => kvp.split('=')));
    }

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return this.oauthService.tryLoginCodeFlow()
        .then(() => {
          console.log("login code flow");
          if (this.oauthService.hasValidAccessToken()) {
            return Promise.resolve();
          }

          // 2. SILENT LOGIN:
          // Try to log in via a refresh because then we can prevent
          // needing to redirect the user:

          return Promise.resolve();

        })

        .then(async () => {
          this.isDoneLoadingSubject$.next(true);

          await this.clearAuthorities();

          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
            let stateUrl = this.oauthService.state;
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);

            this.router.navigateByUrl(stateUrl).then();
          }
        })
        .catch((ex) => {
          Sentry.captureException(ex);
          console.log("Exception while rquesting");
          console.log(ex)
          this.isDoneLoadingSubject$.next(true)
        });
  }

  public login(targetUrl?: string) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initCodeFlow(targetUrl || this.router.url);
  }

  public logout() {
    console.log("User is going to log out")
    this.oauthService.logOut();
  }

  public refresh() {
    this.oauthService.silentRefresh();
  }

  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }

  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }

  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  public get idToken() {
    return this.oauthService.getIdToken();
  }

  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }

  /**
   * Clearing all authorities from the indexedDb if the 'firstLogin' flag ist not yet set in the session storage
   */
  private async clearAuthorities(): Promise<void>{
    const sessionLoginFlag: string | null = sessionStorage.getItem("firstLogin");
    if (!sessionLoginFlag) {
      await this.indexDBService.clear('authorities').toPromise();
      sessionStorage.setItem('firstLogin', 'true');
    }
  }

}
